import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "../../components/Icon";
import { Button, Field } from "../../components/primitives";
import { EMAIL_RE, carryFrom } from "./authUtils";

// SMTP relay ONLY (same endpoint as screens/ForgotPasswordScreen.js). Never add a Firebase reset fallback.
const RESET_URL = "https://api.try-vibelink.com/api/forget-password/";

/** Maps a failure to the same copy as ForgotPasswordScreen.js:80-107. */
function errorCopy(e) {
  const m = (e && e.message) || "";
  if (m.includes("HTTP error") || m.includes("status:")) {
    if (m.includes("404")) return "No account found with this email address.";
    if (m.includes("429")) return "Too many requests. Please wait before trying again.";
    if (m.includes("500")) return "Server error. Please try again later.";
    return m || "Failed to send reset email. Please try again.";
  }
  if (m.includes("Network") || m.includes("fetch")) return "Network error. Please check your connection.";
  return m || "Failed to send reset email. Please try again.";
}

export default function ForgotCard() {
  const keep = carryFrom(useLocation().state);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!email) { setError("Please enter your email address"); return; }
    if (!EMAIL_RE.test(email)) { setError("Please enter a valid email address"); return; }
    setError("");
    setLoading(true);
    try {
      const response = await fetch(RESET_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        let m = `HTTP error! status: ${response.status}`;
        try {
          const data = await response.json();
          m = data.message || data.error || m;
        } catch {
          try {
            const text = await response.text();
            if (text) m = text;
          } catch { /* keep status message */ }
        }
        throw new Error(m);
      }
      await response.json().catch(() => ({}));
      setSent(true);
    } catch (err) {
      setError(errorCopy(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Link className="link row" style={{ gap: 4 }} to="/auth/login" state={keep} data-testid="forgotPassword.back"><Icon name="left" size={16} />Back to sign in</Link>
      <h2 style={{ fontSize: 28, fontWeight: 800 }}>Reset your password</h2>
      <p className="muted">We&apos;ll email a reset link to your @miami.edu address.</p>
      {sent ? (
        <div className="auth-note ok" role="status">
          Check Your Email. We have sent a password reset link to your email address. Please check your inbox and follow the instructions.
        </div>
      ) : (
        <form className="col" style={{ gap: 14 }} onSubmit={submit} noValidate>
          <Field label="Email" type="email" placeholder="you@miami.edu" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="forgotPassword.email" />
          {error && <div className="err-text" role="alert">{error}</div>}
          <Button variant="pri" full type="submit" disabled={loading} data-testid="forgotPassword.submit">{loading ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </>
  );
}
